import React, { Component } from "react";
import Context from "../../Context";
import Strings from "@my-dld/assets/strings";
import { HTAttachment, HTDocLookup, NO, YES, YesNo } from "../../api/entities";
import RadioInputField from "@eres/forms-web/RadioInputField";
import { IdExtractor } from "@my-dld/utils/LookupUtil";
import FormRow from "@eres/forms-web/FormRow";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { loadDocsLookUp } from "../../api/api";
import { HTDocType, HTLookUps } from "../../api/constants";
import { Lookup } from "@eres/ejari-api/Entity";
import TextInputField from "@eres/forms-web/TextInputField";
import Dropzone from "react-dropzone";
import DocumentInputField from "@my-dld/components/Form/DocumentInputField";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { TabuDocTypes } from "@my-dld/utils/TabuUtils";
import { getDocName, getDocSize, getDocType, mapLookupWithAttachments, onDocDrop, setRequest } from "../../Utils";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "@eres/ejari-web/utils";
import { getLocalValue } from "@eres/core/dist/LocaleValue";
import { EjariApi } from "@eres/ejari-api";
import { RenderDocs } from "./RejectionInfo";

interface Props {
	mode: "edit" | "view";
}

export interface DamageData {
	damage?: Lookup;
	description?: string;
	documents: HTDocLookup[];
}

interface State extends DamageData {
	isLoading?: boolean;
	errorContainer: ErrorContainer;
}

class Damages extends Component<Props, State> {
	static contextType = Context;
	context!: React.ContextType<typeof Context>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			documents: [],
			errorContainer: {},
		};
	}

	componentDidMount() {
		const { request } = this.context;
		const damage = request?.IsUnitDamaged ? YES : NO;
		this.setState( {
			damage: damage,
		}, () => {
			if ( damage.Id === YES.Id ) {
				this.loadDocuments();
			}
		} );
	}

	render() {
		if ( this.props.mode === "view" ) {
			return <RenderInfo state={ this.state }/>;
		}
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "bg-light p-2 mb-2 d-inline-block",
		};
		const descProps: Partial<WebFormFieldProps<any, any>> = {
			...filedProps,
			containerClassName: "col-md-12 form-group field-style",
		};
		const { documents, isLoading, damage, errorContainer } = this.state;
		return <div className="card mb-3">
			<div className="card-header">
				<span>{ Strings.property_status_at_time_takeover }</span>
			</div>
			<div className="card-body">
				<FormRow { ...filedProps } containerClassName="col-md-12">
					<RadioInputField
						required={ true }
						id={ "damage" }
						label={ Strings.any_damage_property }
						items={ YesNo }
						toIdentifier={ IdExtractor }
						onValueChange={ this.onValueChange }/>
				</FormRow>
				{ isLoading && <span className="spinner-border"/> }
				{ !isLoading && damage?.Id === YES.Id && <>
					<FormRow { ...descProps }>
						<TextInputField
							required={ true }
							id={ "description" }
							label={ Strings.provide_description }
							customInput={ <textarea rows={ 4 }/> }
							maxLength={ 500 }/>
					</FormRow>
					<FormRow>
						<RenderDocumentCategory
							documents={ documents }
							onRejected={ this.onRejected }
							onDrop={ this.onDrop }
							onDelete={ this.onDelete }
							errorContainer={ errorContainer }/>
					</FormRow>
				</> }
			</div>
		</div>;
	}

	public getDamages(): Promise<DamageData> {
		const error: ErrorContainer = {};
		const { damage, description, documents } = this.state;
		let isValid = true;
		if ( damage?.Id === YES.Id ) {
			isValid = checkNonEmpty( this.state, "description", error ) && isValid;
			documents.forEach( ( doc: HTDocLookup ) => {
				if ( doc.IsMandatory === 1 && ( doc.docs == undefined || doc.docs.length === 0 ) ) {
					error[ "" + doc.Identity ] = Strings.required;
					isValid = false;
				}
			} );
			this.setState( {
				errorContainer: error,
			} );
		}
		if ( isValid ) {
			return Promise.resolve( {
				damage: damage,
				description: description,
				documents: documents,
			} );
		}
		return Promise.reject( "Damages not valid" );
	}

	private onValueChange = ( type?: Lookup ) => {
		if ( type?.Id === YES.Id && this.state.documents.length === 0 ) {
			this.loadDocuments();
		}
	};

	private loadDocuments() {
		this.setState( { isLoading: true } );
		loadDocsLookUp( HTLookUps.ATTACHMENTS, HTDocType.DAMAGES ).then( docs => {
			const { request } = this.context;
			const documents = docs || [];
			const state: Partial<State> = {
				documents: documents,
			};
			if ( request && request.Damages && request.Damages.length > 0 ) {
				const damage = request.Damages[ 0 ];
				mapLookupWithAttachments( documents, damage.DamageAttachments );
				state.description = damage.Description;
				state.documents = documents;
			}
			this.setState( {
				...state,
				isLoading: false,
			} as State );
		} );
	}

	private onRejected = () => {
		showErrorMessage( Strings.ht_error_total_file_size );
	};

	private onDrop = ( doc: HTDocLookup, files: File[] ) => {
		onDocDrop( doc, files, ( sDoc: HTDocLookup ) => {
			const { documents, errorContainer } = this.state;
			delete errorContainer[ "" + sDoc.Identity ];
			this.setState( {
				documents: documents.map( ( doc: HTDocLookup ) => {
					return ( sDoc.Identity === doc.Identity ) ? { ...doc, docs: sDoc.docs } : doc;
				} ),
				errorContainer: errorContainer,
			} );
		} );
	};

	private onDelete = ( attachment: HTAttachment, index: number ) => {
		if ( attachment.Identity && attachment.Identity !== 0 ) {
			showLoading();
			EjariApi.deleteTenancyContractDocument( attachment.Identity ).then( () => {
				this.updateDeleteDocs( attachment, index );
				hideLoading();
			} ).catch( reason => {
				hideLoading();
				showErrorMessage( reason );
			} );
		} else {
			this.updateDeleteDocs( attachment, index );
		}
	};

	private updateDeleteDocs( attachment: HTAttachment, index: number ) {
		const { documents } = this.state;
		this.setState( {
			documents: documents.map( ( doc: HTDocLookup ) => {
				if ( doc.Identity === attachment.DocumentId ) {
					doc.docs!.splice( index, 1 );
					return doc;
				}
				return doc;
			} ),
		}, () => {
			const { request } = this.context;
			if ( request && request.Damages && request.Damages.length > 0 ) {
				const attachments: HTAttachment[] = [];
				documents.forEach( doc => attachments.push( ...doc.docs || [] ) );
				request.Damages[ 0 ].DamageAttachments = attachments;
				setRequest( request );
			}
		} );
	}
}

export function RenderDocumentCategory( props: {
	documents: HTDocLookup[], onRejected: () => void,
	onDrop: ( doc: HTDocLookup, files: File[] ) => void,
	onDelete: ( doc: HTAttachment, index: number ) => void,
	errorContainer: ErrorContainer;
} ) {
	const { documents, onRejected, onDrop, onDelete, errorContainer } = props;
	return <>
		{ documents.map( ( doc: HTDocLookup, index: number ) => {
			const isDocsAdded = ( doc.docs && doc.docs.length > 0 );
			const maxPages = doc.MaxPages ? doc.MaxPages : 1;
			const totalSizeBytes = doc.MaxSizeKb ? ( doc.MaxSizeKb * 1000 ) : ( 8 * 1024 * 1000 );
			let availableSizeBytes = totalSizeBytes;
			if ( isDocsAdded ) {
				let uploadedSizeBytes = 0;
				doc.docs!.forEach( ( a: HTAttachment ) => {
					uploadedSizeBytes += ( a.ActualSizeKb! * 1000 );
				} );
				availableSizeBytes = Number( ( totalSizeBytes - uploadedSizeBytes ).toFixed( 2 ) );
			}
			const totalSizeMB = Number( ( totalSizeBytes / ( 1024 * 1000 ) ).toFixed( 0 ) );
			return <div
				key={ `${ index }-${ doc.Identity }` }
				className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
				<Dropzone
					multiple={ maxPages > 1 }
					maxSize={ availableSizeBytes }
					onDropRejected={ onRejected }
					onDrop={ ( files: File[] ) => {
						onDrop( doc, files );
					} }
					accept={ [ "image/png", "image/jpeg", "application/pdf" ] }>
					{ ( { getRootProps, getInputProps } ) => (
						<>
							<DocumentInputField { ...getRootProps() }
							                    dropZoneInputProps={ getInputProps() }
							                    required={ doc.IsMandatory === 1 }
							                    label={ getLocalValue( doc ) }
							                    isDocsAdded={ isDocsAdded }
							                    error={ errorContainer[ "" + doc.Identity ] }
							                    disabled={ ( doc.docs && doc.docs.length === maxPages ) }/>
							<small>{ Strings.ht_total_file_size_note.replace( "~", "" + totalSizeMB ) }</small><br/>
							<small>{ Strings.max_num_files + " : " + doc.MaxPages }</small>
						</>
					) }
				</Dropzone>
				<RenderDocuments docLookup={ doc } onDelete={ onDelete }/>
			</div>;
		} ) }
	</>;
}

function RenderDocuments( props: { docLookup: HTDocLookup, onDelete: ( doc: HTAttachment, index: number ) => void } ) {
	const { docLookup, onDelete } = props;
	if ( docLookup.docs && docLookup.docs.length > 0 ) {
		return <div className={ "mb-2" }>
			{ docLookup.docs.map( ( doc: HTAttachment, index: number ) => {
				const docType = getDocType( doc );
				const preview = ( docType === TabuDocTypes.PDF ) ? <i className="far fa-file-pdf"/> : getDocPreview( doc );
				const docName = getDocName( docLookup, doc )?.replace( "~", "" + ( index + 1 ) );
				return <table
					key={ `${ docName }_${ index }` }
					className="table table-bordered table-condensed table-striped table-responsive-sm upload-table">
					<thead>
					<tr>
						<th>{ Strings.file }</th>
						<th>{ Strings.name }</th>
						<th>{ Strings.size }</th>
						<th>{ Strings.action }</th>
					</tr>
					</thead>
					<tbody>
					<tr>
						<td data-label={ Strings.file }>{ preview }</td>
						<td data-label={ Strings.name }>{ docName }</td>
						<td data-label={ Strings.size }>{ `${ getDocSize( doc ) } ${ Strings.size_mb }` }</td>
						<td data-label={ Strings.action }>
							<button
								className="btn btn-grey" onClick={ () => {
								onDelete( doc, index );
							} }><i className="far fa-trash-alt"/>{ Strings.delete }
							</button>
						</td>
					</tr>
					</tbody>
				</table>;
			} )
			}
		</div>;
	}
	return null;
}

function getDocPreview( doc: HTAttachment ) {
	let source = ( doc.LocalData && !isEmptyStringOrNull( doc.LocalData ) ) ?
		doc.LocalData : doc.FileUrl;
	return <img src={ source } style={ { height: 40, width: 40 } } alt={ "uploaded_image" }/>;
}

function RenderInfo( props: { state: State } ) {
	const { damage, description, documents } = props.state;
	return <>
		<div className="section-title">
			<h5>{ Strings.property_status_at_time_takeover }</h5>
		</div>
		<div className="flex-grid flex-full">
			<div>
				<span>{ Strings.any_damage_property }</span>
				<span>{ getLocalValue( damage ) }</span>
			</div>
			{ damage?.Id === YES.Id && <>
				<div>
					<span>{ Strings.description }</span>
					<span>{ description }</span>
				</div>
				<RenderDocs documents={ documents }/>
			</> }
		</div>
	</>;
}

export default Damages;
